import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import img1 from "../../../Images/logo.webp";
import CustomLogoImage from "../../shared/CustomLogoImage";
import Birthday from "../../questionscomponent/subcomponent/Birthday";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^\(\d{3}\)-\d{3}-\d{4}$/;
const NAME_REGEX = /^.{1,}$/;
const SALES_REP_NAME_REGEX = /^.{3,}$/;

function formatPhoneNumber(digitsOnly) {
  if (!digitsOnly) return "";
  if (digitsOnly.length <= 3) return `(${digitsOnly}`;
  if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3)}`;
  }
  return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
}

function isDobValid(dob) {
  if (!dob) return false;
  const inputDate = new Date(dob);
  const currentDate = new Date();
  return inputDate <= currentDate;
}

/**
 * PD-6 Step 1 "You".
 *
 * Merges the fields previously spread across their own screens
 * (Email.js, phone.js, Username.js, Middlename.js, Lastname.js, dob.js and,
 * for sales-rep-flow tenants, HasSalesRep.js) into one screen, shared
 * identically across the business and personal paths.
 *
 * Each field keeps its own individual `partial-application` autosave POST
 * call with the exact same payload shape as its original screen — this
 * component only decides *when* to fire them (on Continue) and where to
 * navigate afterwards.
 */
const StepYou = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const [email, setEmail] = useState(state?.questions?.email ?? "");
  const [phone, setPhone] = useState(() => {
    const stored = state?.questions?.phone ?? "";
    const digits = stored.replace("+1", "").replace(/[^0-9]/g, "");
    return formatPhoneNumber(digits);
  });
  const [firstName, setFirstName] = useState(state?.questions?.firstname ?? "");
  const [lastName, setLastName] = useState(state?.questions?.lastname ?? "");
  const [purpleWaveCustomerNumber, setPurpleWaveCustomerNumber] = useState(
    state?.questions?.purpleWaveCustomerNumber ?? ""
  );
  // Only the Purple Wave company's links (internal or third-party) ask for
  // this field — decided by the backend from PURPLE_WAVE_COMPANY_ID.
  const isPurpleWaveListing = Boolean(state?.isPurpleWaveAccount);

  // `state.isPersonalFlow` is a server-side permission flag ("this
  // company/link is allowed to run the personal path"), not the borrower's
  // choice — so this question only appears for companies that support both
  // paths. "Business" is pre-selected by default on first load (matching
  // the design), and only "Personal" is treated as an explicit resumed
  // choice — real resumed values land here via GlobalState.js's initialState
  // / the save-for-later resume flow.
  const isPersonalFlowEnabled = Boolean(state?.isPersonalFlow);
  const [equipmentUse, setEquipmentUse] = useState(() => {
    return state?.questions?.equipmentUse === "Personal" ? "Personal" : "Business";
  });

  const isSalesRepFlow = Boolean(state?.isSalesRepFlow);
  const [hasSalesRep, setHasSalesRep] = useState(state?.questions?.hasSalesRep ?? false);
  const [salesRepFirstName, setSalesRepFirstName] = useState(
    state?.questions?.salesRepFirstName ?? ""
  );
  const [salesRepLastName, setSalesRepLastName] = useState(
    state?.questions?.salesRepLastName ?? ""
  );

  // "Just saved now": every field on this step lands in global state as the
  // borrower types/selects, not only once they click Continue — so
  // navigating Back then Continue again restores whatever was filled in,
  // instead of losing it because this component's local state was
  // discarded on unmount. One consolidated dispatch (not one effect per
  // field) avoids the multi-dispatch stale-closure clobbering bug documented
  // in StepEquipment.js's saveEquipment — every field here shares the same
  // QUESTIONS (top-level) dispatch target, so there's only ever one to fire.
  useEffect(() => {
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        email,
        phone: "+1" + phone.replace(/[^0-9]/g, ""),
        firstname: firstName,
        lastname: lastName,
        equipmentUse: isPersonalFlowEnabled ? equipmentUse : state?.questions?.equipmentUse,
        hasSalesRep: isSalesRepFlow ? hasSalesRep : state?.questions?.hasSalesRep,
        salesRepFirstName: isSalesRepFlow && hasSalesRep ? salesRepFirstName : "",
        salesRepLastName: isSalesRepFlow && hasSalesRep ? salesRepLastName : "",
        purpleWaveCustomerNumber,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, phone, firstName, lastName, equipmentUse, hasSalesRep, salesRepFirstName, salesRepLastName, purpleWaveCustomerNumber]);

  const emailRef = useRef();

  useEffect(() => {
    if (emailRef.current) emailRef.current.focus();
  }, []);

  const validate = () => {
    const errors = {};
    if (!email || !EMAIL_REGEX.test(email)) errors.email = "Please provide a valid email.";
    if (!phone || !PHONE_REGEX.test(phone)) {
      errors.phone = "Please provide a valid US phone number e.g. (234)-567-8900";
    }
    if (!firstName || !NAME_REGEX.test(firstName)) errors.firstName = "Please enter a valid first name";
    if (!lastName || !NAME_REGEX.test(lastName)) errors.lastName = "Please enter a valid last name";
    if (!isDobValid(state?.questions?.dob)) errors.dob = "Please choose a valid date.";
    if (isPersonalFlowEnabled && !equipmentUse) {
      errors.equipmentUse = "Please select an option.";
    }
    if (isSalesRepFlow && hasSalesRep) {
      if (!salesRepFirstName || !SALES_REP_NAME_REGEX.test(salesRepFirstName.trim())) {
        errors.salesRepFirstName = "Please enter a valid first name (min 3 characters)";
      }
      if (!salesRepLastName || !SALES_REP_NAME_REGEX.test(salesRepLastName.trim())) {
        errors.salesRepLastName = "Please enter a valid last name (min 3 characters)";
      }
    }
    return errors;
  };

  const [errors, setErrors] = useState({});

  // Exposed to StepShell.js's "Save & finish later" handler — runs this
  // step's own validation before attempting the save, same as a failed
  // Continue click (inline field errors + the summary banner below), so an
  // incomplete step never silently reaches the save-for-later request.
  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      const validationErrors = validate();
      setErrors(validationErrors);
      setSubmitAttempted(true);
      return Object.keys(validationErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press.
  useEffect(() => {
    if (submitAttempted) setErrors(validate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submitAttempted,
    email,
    phone,
    firstName,
    lastName,
    state?.questions?.dob,
    equipmentUse,
    hasSalesRep,
    salesRepFirstName,
    salesRepLastName,
  ]);

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly.length <= 10) {
      setPhone(formatPhoneNumber(digitsOnly));
    }
  };

  const handleSalesRepOptionChange = (value) => {
    setHasSalesRep(value);
    if (!value) {
      setSalesRepFirstName("");
      setSalesRepLastName("");
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setServerError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const updatedPhone = "+1" + phone.replace(/[^0-9]/g, "");

    // Single QUESTIONS dispatch merging the edited fields (dob is already
    // kept up to date by Birthday's own effect).
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        email,
        phone: updatedPhone,
        firstname: firstName,
        lastname: lastName,
        equipmentUse: isPersonalFlowEnabled ? equipmentUse : state?.questions?.equipmentUse,
        hasSalesRep: isSalesRepFlow ? hasSalesRep : state?.questions?.hasSalesRep,
        salesRepFirstName: isSalesRepFlow && hasSalesRep ? salesRepFirstName.trim() : "",
        salesRepLastName: isSalesRepFlow && hasSalesRep ? salesRepLastName.trim() : "",
        purpleWaveCustomerNumber: purpleWaveCustomerNumber.trim(),
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

    setShowLoader(true);
    try {
      // Each field keeps its own autosave call, matching the payload shape
      // of its original standalone screen. These are independent fields, so
      // fire them concurrently instead of one round trip at a time.
      const requests = [
        axios.post(endpoint, { email }),
        axios.post(endpoint, { phone_no: updatedPhone }),
        axios.post(endpoint, { first_name: firstName }),
        axios.post(endpoint, { last_name: lastName }),
        axios.post(endpoint, { date_of_birth: state?.questions?.dob }),
      ];
      if (isPurpleWaveListing) {
        requests.push(
          axios.post(endpoint, { purple_wave_customer_number: purpleWaveCustomerNumber.trim() })
        );
      }
      if (isPersonalFlowEnabled) {
        requests.push(axios.post(endpoint, { equipment_use: equipmentUse }));
      }
      if (isSalesRepFlow) {
        const salesRepPayload = { has_sales_rep: hasSalesRep };
        if (hasSalesRep) {
          salesRepPayload.sales_rep_first_name = salesRepFirstName.trim();
          salesRepPayload.sales_rep_last_name = salesRepLastName.trim();
        }
        requests.push(axios.post(endpoint, salesRepPayload));
      }
      await Promise.all(requests);
      goNext();
    } catch (error) {
      setServerError(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">Let's start with you</h2>
      <p className="pd6-step__helper">
        Five details and what the equipment is for. Nothing here affects your credit score.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <Form.Group className="pd6-field" controlId="stepYouEmail">
          <Form.Label>
            Email<span className="pd6-required">*</span>
          </Form.Label>
          <Form.Control
            ref={emailRef}
            type="email"
            autoComplete="off"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            isInvalid={submitAttempted && Boolean(errors.email)}
          />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepYouPhone">
            <Form.Label>
              Mobile phone<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="tel"
              inputMode="numeric"
              placeholder="(555)-555-5555"
              value={phone}
              onChange={handlePhoneChange}
              isInvalid={submitAttempted && Boolean(errors.phone)}
            />
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepYouDob">
            <Form.Label>
              Date of birth<span className="pd6-required">*</span>
            </Form.Label>
            <Birthday />
            {submitAttempted && errors.dob && (
              <div className="pd6-field-error">{errors.dob}</div>
            )}
          </Form.Group>
        </div>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepYouFirstName">
            <Form.Label>
              First name<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.firstName)}
            />
            <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepYouLastName">
            <Form.Label>
              Last name<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.lastName)}
            />
            <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
          </Form.Group>
        </div>

        {isPurpleWaveListing && (
          <Form.Group className="pd6-field" controlId="stepYouPurpleWaveCustomerNumber">
            <Form.Label>Purple Wave Customer Number</Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              value={purpleWaveCustomerNumber}
              onChange={(e) => setPurpleWaveCustomerNumber(e.target.value)}
            />
            <div className="pd6-field-helper">
              <span>
                Optional — found in your Purple Wave account. Adding it helps us confirm your bid and move your approval faster.
              </span>
            </div>
          </Form.Group>
        )}

        {isPersonalFlowEnabled && (
          <Form.Group className="pd6-field" controlId="stepYouEquipmentUse">
            <Form.Label>
              How will you use the equipment?
              <span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              <button
                type="button"
                className={`pd6-chip ${equipmentUse === "Business" ? "pd6-chip--selected" : ""}`}
                onClick={() => setEquipmentUse("Business")}
              >
                Business
              </button>
              <button
                type="button"
                className={`pd6-chip ${equipmentUse === "Personal" ? "pd6-chip--selected" : ""}`}
                onClick={() => setEquipmentUse("Personal")}
              >
                Personal
              </button>
            </div>
            {submitAttempted && errors.equipmentUse && (
              <div className="pd6-field-error">{errors.equipmentUse}</div>
            )}
            <div style={{ fontSize: "12px", color: "#8a8a92", marginTop: "6px", lineHeight: 1.45 }}>
              This decides what we ask for next.
            </div>
          </Form.Group>
        )}

        {isSalesRepFlow && (
          <Form.Group className="pd6-field" controlId="stepYouHasSalesRep">
            <Form.Label>
              Do you have a sales representative you are working with?
              <span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              <button
                type="button"
                className={`pd6-chip ${hasSalesRep ? "pd6-chip--selected" : ""}`}
                onClick={() => handleSalesRepOptionChange(true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`pd6-chip ${!hasSalesRep ? "pd6-chip--selected" : ""}`}
                onClick={() => handleSalesRepOptionChange(false)}
              >
                No
              </button>
            </div>

            {hasSalesRep && (
              <div className="pd6-reveal-panel">
                <div className="pd6-field-row">
                  <Form.Group className="pd6-field" controlId="salesRepFirstName">
                    <Form.Label>Representative first name</Form.Label>
                    <Form.Control
                      type="text"
                      value={salesRepFirstName}
                      onChange={(e) => setSalesRepFirstName(e.target.value)}
                      isInvalid={submitAttempted && Boolean(errors.salesRepFirstName)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.salesRepFirstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="salesRepLastName">
                    <Form.Label>Representative last name</Form.Label>
                    <Form.Control
                      type="text"
                      value={salesRepLastName}
                      onChange={(e) => setSalesRepLastName(e.target.value)}
                      isInvalid={submitAttempted && Boolean(errors.salesRepLastName)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.salesRepLastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            )}
          </Form.Group>
        )}

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="pd6-form-summary-error">
            {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} still
            need{Object.keys(errors).length === 1 ? "s" : ""} an answer.
          </div>
        )}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            {!isFirstStep && (
              <button
                type="button"
                className="pd6-btn pd6-btn--ghost"
                onClick={goBack}
                disabled={showLoader}
              >
                <span className="pd6-btn__arrow">←</span>
                <span>Back</span>
              </button>
            )}
            <button type="submit" className="pd6-btn pd6-btn--primary" disabled={showLoader}>
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepYou;
